import { POPFRAME_DELIVERY_ORIGIN } from '@repo/utils';
import { GENERATED_PRINT_FRAME_SCENES } from './print-frame-scenes.generated.js';
import type { PrintSceneSideWrap, PrintSceneManifest } from './print-scene-renderer.js';
import {
  getDefaultPrintSizeForAspectRatio,
  getPrintRatioCatalogEntry,
} from './print-ratio-catalog.js';

const slug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const getPrintSceneAsset = (size?: string | null, frame?: string | null) => {
  if (!size || !frame) return null;
  const key = `${slug(size)}__${slug(frame)}`;
  const entry = GENERATED_PRINT_FRAME_SCENES[key];
  if (!entry) return null;
  return {
    key,
    baseUrl: `${POPFRAME_DELIVERY_ORIGIN}/images/print/frame-scenes/v1/${key}/${entry.revision}`,
  };
};

/** Shared scene choice for browser previews and background image exports. */
export const getBlackFramePreviewScene = (aspectRatio: string) => {
  const catalog = getPrintRatioCatalogEntry(aspectRatio);
  if (!catalog) return null;
  const sizes = [getDefaultPrintSizeForAspectRatio(aspectRatio), ...catalog.printSizes];
  for (const canvasSize of sizes) {
    const asset = getPrintSceneAsset(canvasSize, 'Black Floating Frame');
    if (asset && canvasSize) {
      return { asset, canvasSize, ratio: catalog.preview.width / catalog.preview.height };
    }
  }
  return null;
};

export const validatePrintSceneManifest = (value: unknown, key: string): PrintSceneManifest => {
  const manifest = <Partial<PrintSceneManifest> | null>value;
  const expected = ['front', 'side', 'context-1', 'context-2'];
  if (
    !manifest ||
    manifest.schemaVersion !== 1 ||
    manifest.key !== key ||
    typeof manifest.size !== 'string' ||
    typeof manifest.frame !== 'string' ||
    `${slug(manifest.size)}__${slug(manifest.frame)}` !== key ||
    !Array.isArray(manifest.views) ||
    manifest.views.length !== expected.length
  )
    throw new Error('Invalid print scene manifest');
  for (const [index, view] of manifest.views.entries()) {
    if (
      !view ||
      view.id !== expected[index] ||
      !Number.isInteger(view.width) ||
      !Number.isInteger(view.height) ||
      view.width <= 0 ||
      view.height <= 0 ||
      view.width > 2400 ||
      view.height > 2400 ||
      !Array.isArray(view.inverse) ||
      view.inverse.length !== 9 ||
      !view.inverse.every(Number.isFinite) ||
      view.blackFile !== `${view.id}-black.jpg` ||
      view.whiteFile !== `${view.id}-white.jpg`
    )
      throw new Error('Invalid print scene geometry');
    const [a, b, c, d, e, f, g, h, i] = view.inverse;
    const determinant =
      a! * (e! * i! - f! * h!) - b! * (d! * i! - f! * g!) + c! * (d! * h! - e! * g!);
    if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-10)
      throw new Error('Singular print scene geometry');
  }
  return <PrintSceneManifest>manifest;
};

/** Side geometry belongs to an exact immutable template revision. */
export const getPrintSceneSideWrap = (key: string): PrintSceneSideWrap | undefined =>
  GENERATED_PRINT_FRAME_SCENES[key]?.sideWrap;
